import type { Perfil } from '../domain/entities/Perfil';
import { crearError, ErrorApp, registrarError, traducirErrorSupabase } from '../domain/errores';
import { repositorioPerfil, RepositorioPerfil } from '../data/repositories/RepositorioPerfil';
import { supabase } from '../data/supabase/cliente';
import { aObjeto, aTexto } from '../data/mappers/conversiones';
import { env, STORAGE_PREFIX } from '../config/env';

/** Duración de una sesión que no se pidió recordar. */
const DURACION_SESION_MS = 2 * 60 * 60 * 1000; // 2 horas

const CLAVE_RECORDAR = `${STORAGE_PREFIX}recordar-sesion`;
const CLAVE_INICIO = `${STORAGE_PREFIX}inicio-sesion`;

export interface CredencialesAcceso {
  email: string;
  password: string;
  /** Mantiene la sesión abierta más allá del límite de dos horas. */
  recordar: boolean;
}

/**
 * Autenticación y ciclo de vida de la sesión.
 *
 * Reúne en un solo sitio lo que antes estaba repartido: iniciar sesión,
 * comprobar que el perfil puede operar, vigilar la caducidad y cerrar sesión
 * dejando el navegador limpio.
 */
export class ServicioAutenticacion {
  constructor(private readonly perfiles: RepositorioPerfil = repositorioPerfil) {}

  /**
   * Inicia sesión y devuelve el perfil.
   *
   * Un usuario bloqueado o inactivo no debe quedarse con la sesión abierta,
   * así que se cierra antes de informar del rechazo.
   */
  async iniciarSesion({ email, password, recordar }: CredencialesAcceso): Promise<Perfil> {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password,
    });

    if (error) throw this.traducirErrorAcceso(error);
    if (!data.session) throw crearError('CREDENCIALES_INVALIDAS');

    let perfil: Perfil;
    try {
      perfil = await this.perfiles.obtenerMiPerfil(data.user?.email);
    } catch (e) {
      await this.cerrarSesionSilenciosa();
      throw e;
    }

    const motivo = perfil.motivoAccesoDenegado;
    if (motivo) {
      await this.cerrarSesionSilenciosa();
      throw new ErrorApp(perfil.bloqueado ? 'USUARIO_BLOQUEADO' : 'USUARIO_INACTIVO', motivo);
    }

    this.registrarInicio(recordar);
    return perfil;
  }

  /**
   * Recupera la sesión guardada al abrir la aplicación.
   * Devuelve `null` si no hay sesión, si caducó o si el perfil ya no puede operar.
   */
  async recuperarSesion(): Promise<Perfil | null> {
    const { data, error } = await supabase.auth.getSession();

    if (error) {
      registrarError('auth.recuperarSesion', error);
      return null;
    }
    if (!data.session) return null;

    if (this.sesionCaducada()) {
      await this.cerrarSesion();
      return null;
    }

    try {
      const perfil = await this.perfiles.obtenerMiPerfil(data.session.user.email);

      if (!perfil.puedeAcceder) {
        await this.cerrarSesion();
        return null;
      }
      return perfil;
    } catch (e) {
      registrarError('auth.recuperarSesion.perfil', e);
      await this.cerrarSesion();
      return null;
    }
  }

  /**
   * Abre una sesión de demostración.
   *
   * Las credenciales no están aquí ni en el paquete que descarga el navegador:
   * viven como secreto de una función del servidor, que es quien decide si
   * concede la sesión y devuelve los tokens ya emitidos. Así la cuenta de
   * muestra no se puede usar para entrar por la puerta principal.
   */
  async entrarEnModoDemostracion(): Promise<Perfil> {
    const { data, error } = await supabase.functions.invoke('demo-session');

    if (error) {
      registrarError('acceso.demostracion', error);
      throw crearError('DESCONOCIDO', error, 'La demostración no está disponible ahora mismo.');
    }

    const respuesta = aObjeto(data);
    const accessToken = aTexto(respuesta.access_token);
    const refreshToken = aTexto(respuesta.refresh_token);

    if (!accessToken || !refreshToken) {
      const detalle = aTexto(respuesta.error);
      throw crearError(
        'DESCONOCIDO',
        data,
        detalle ?? 'La demostración no está disponible ahora mismo.',
      );
    }

    const { error: errorSesion } = await supabase.auth.setSession({
      access_token: accessToken,
      refresh_token: refreshToken,
    });

    if (errorSesion) throw this.traducirErrorAcceso(errorSesion);

    const perfil = await this.perfiles.obtenerMiPerfil();
    const motivo = perfil.motivoAccesoDenegado;

    if (motivo) {
      await this.cerrarSesionSilenciosa();
      throw new ErrorApp(perfil.bloqueado ? 'USUARIO_BLOQUEADO' : 'USUARIO_INACTIVO', motivo);
    }

    // La demostración no se recuerda: es para probar, no para trabajar.
    this.registrarInicio(false);
    return perfil;
  }

  /** Vuelve a leer el perfil, por ejemplo tras cambiar la contraseña. */
  async refrescarPerfil(): Promise<Perfil> {
    const { data } = await supabase.auth.getUser();
    return this.perfiles.obtenerMiPerfil(data.user?.email);
  }

  async cerrarSesion(): Promise<void> {
    await this.cerrarSesionSilenciosa();
  }

  /** Envía el correo de recuperación con el enlace de vuelta a esta aplicación. */
  async solicitarRecuperacion(email: string): Promise<void> {
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
      redirectTo: new URL('/restablecer-clave', window.location.origin).toString(),
    });
    if (error) throw traducirErrorSupabase(error);
  }

  /** Fija una contraseña nueva para la sesión en curso. */
  async cambiarClave(nueva: string): Promise<void> {
    const { error } = await supabase.auth.updateUser({ password: nueva });

    if (error) {
      const mensaje = error.message ?? '';
      if (mensaje.includes('should be different')) {
        throw crearError(
          'DATOS_INVALIDOS',
          error,
          'La contraseña nueva debe ser distinta de la anterior.',
        );
      }
      throw traducirErrorSupabase(error);
    }

    // Si el cambio era obligatorio, esto levanta la restricción.
    await this.perfiles.limpiarBanderaCambioClave().catch((e) => {
      registrarError('auth.limpiarBanderaCambioClave', e);
    });
  }

  /**
   * Comprueba que el servidor habla el mismo contrato de datos.
   * Si no coincide, la aplicación debe bloquearse en vez de mostrar datos que
   * podría estar interpretando mal.
   */
  async verificarCompatibilidad(): Promise<void> {
    let etiquetaServidor: string | null;
    try {
      etiquetaServidor = await this.perfiles.obtenerEtiquetaBackend();
    } catch (e) {
      // Si la comprobación no se puede hacer, no se bloquea al usuario: se
      // registra y se sigue. Bloquear por un fallo de red sería peor.
      registrarError('auth.verificarCompatibilidad', e);
      return;
    }

    if (etiquetaServidor && etiquetaServidor !== env.backendTag) {
      throw crearError('BACKEND_INCOMPATIBLE');
    }
  }

  /** Escucha los cambios de sesión que ocurren fuera de la aplicación. */
  alCambiarSesion(manejador: (haySesion: boolean) => void): () => void {
    const { data } = supabase.auth.onAuthStateChange((evento, sesion) => {
      if (evento === 'SIGNED_OUT') manejador(false);
      else if (evento === 'SIGNED_IN' || evento === 'TOKEN_REFRESHED') manejador(Boolean(sesion));
    });

    return () => data.subscription.unsubscribe();
  }

  // ---------------------------------------------------------------------------

  private registrarInicio(recordar: boolean): void {
    localStorage.setItem(CLAVE_RECORDAR, String(recordar));
    localStorage.setItem(CLAVE_INICIO, String(Date.now()));
  }

  /** `true` si la sesión superó el límite de dos horas sin haberse recordado. */
  private sesionCaducada(): boolean {
    if (localStorage.getItem(CLAVE_RECORDAR) === 'true') return false;

    const inicio = Number(localStorage.getItem(CLAVE_INICIO));
    if (!Number.isFinite(inicio) || inicio <= 0) return false;

    return Date.now() - inicio > DURACION_SESION_MS;
  }

  private async cerrarSesionSilenciosa(): Promise<void> {
    try {
      await supabase.auth.signOut();
    } catch (e) {
      registrarError('auth.cerrarSesion', e);
    }
    this.limpiarAlmacenamientoPropio();
  }

  /**
   * Borra únicamente lo que guarda esta aplicación.
   *
   * La versión anterior llamaba a `localStorage.clear()`, que arrasaba también
   * con datos de otras aplicaciones servidas desde el mismo dominio.
   */
  private limpiarAlmacenamientoPropio(): void {
    for (const almacen of [localStorage, sessionStorage]) {
      const claves = Object.keys(almacen).filter((k) => k.startsWith(STORAGE_PREFIX));
      for (const clave of claves) almacen.removeItem(clave);
    }
  }

  /** Traduce los errores de acceso a mensajes que el usuario pueda accionar. */
  private traducirErrorAcceso(error: { message?: string; status?: number }): ErrorApp {
    const mensaje = (error.message ?? '').toLowerCase();

    if (mensaje.includes('email not confirmed')) return crearError('EMAIL_NO_CONFIRMADO', error);
    if (mensaje.includes('invalid login credentials')) {
      return crearError('CREDENCIALES_INVALIDAS', error);
    }
    if (mensaje.includes('failed to fetch')) return crearError('SIN_CONEXION', error);

    return traducirErrorSupabase(error);
  }
}

export const servicioAutenticacion = new ServicioAutenticacion();
